import type {
  MeResponse,
  MediaListResponse,
  MediaDetail,
  TagSummary,
  UploadOk,
  UploadDuplicate,
  StructuredFilter,
  AiStatus,
  AiModel,
  AiGeneration,
  AiGenerateRequest,
} from "@media-viewer/shared";
import { hashFileSha256 } from "./hash";

export interface AppConfig {
  appName: string;
  appNameLocked: boolean;
  homeRefreshSeconds: number;
  homeRefreshLocked: boolean;
  accountUrl: string | null;
  authMode: "oidc" | "local" | "dev";
  aiGenerationEnabled: boolean;
}

export interface NeighborsResponse {
  prev: number | null;
  next: number | null;
  first: number | null;
  currentKind: "photo" | "video";
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`api error ${status}`);
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: "include", ...init });
  if (!r.ok) {
    let body: unknown = null;
    try {
      body = await r.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(r.status, body);
  }
  return (await r.json()) as T;
}

/** Base64-json a filter for transport, or undefined when it has no real criteria. */
export function encodeFilterParam(filter: StructuredFilter | null | undefined): string | undefined {
  if (!filter) return undefined;
  const meaningful = Object.entries(filter).some(([k, v]) => {
    if (k === "rawQuery" || k === "warnings") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object") return Object.values(v).some((x) => x != null);
    return v != null;
  });
  if (!meaningful) return undefined;
  return btoa(unescape(encodeURIComponent(JSON.stringify(filter))));
}

function appendFilterParam(url: URL, filter: StructuredFilter | null): void {
  const b64 = encodeFilterParam(filter);
  if (b64) url.searchParams.set("filter", b64);
}

export async function parseSearchQuery(
  q: string,
  pageContext: { kind?: "photo" | "video" | null; tag?: string | null } = {},
): Promise<{ filter: StructuredFilter }> {
  const res = await fetch("/api/search/parse", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ q, pageContext }),
  });
  if (!res.ok) {
    if (res.status === 429) throw new Error("rate_limited");
    throw new Error(`parse failed: ${res.status}`);
  }
  return res.json();
}

export const api = {
  config: () => fetchJson<AppConfig>("/api/config"),
  me: () => fetchJson<MeResponse>("/api/me"),
  localLogin: (username: string, password: string) =>
    fetchJson<{ ok: true }>(`/api/auth/local-login`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password }),
    }),
  localChangePassword: (currentPassword: string, newPassword: string) =>
    fetchJson<{ ok: true }>(`/api/auth/local-change-password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  listMedia: (params: {
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    before?: string;
    limit?: number;
    q?: string;
    order?: "random" | "newest" | "oldest" | "added_new" | "added_old" | "favorited_at";
    seed?: string;
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
    set?: "favorites";
  }) => {
    const url = new URL("/api/media", location.origin);
    if (params.kind) url.searchParams.set("kind", params.kind);
    if (params.tag) url.searchParams.set("tag", params.tag);
    if (params.tags && params.tags.length > 0) url.searchParams.set("tags", params.tags.join(","));
    if (params.excludeTags && params.excludeTags.length > 0)
      url.searchParams.set("excludeTags", params.excludeTags.join(","));
    if (params.before) url.searchParams.set("before", params.before);
    if (params.limit) url.searchParams.set("limit", String(params.limit));
    if (params.q) url.searchParams.set("q", params.q);
    if (params.order) url.searchParams.set("order", params.order);
    if (params.seed) url.searchParams.set("seed", params.seed);
    if (params.minSeconds != null) url.searchParams.set("minSeconds", String(params.minSeconds));
    if (params.maxSeconds != null) url.searchParams.set("maxSeconds", String(params.maxSeconds));
    if (params.set) url.searchParams.set("set", params.set);
    appendFilterParam(url, params.structured ?? null);
    return fetchJson<MediaListResponse>(url.pathname + url.search);
  },
  countMedia: (params: {
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    q?: string;
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
    set?: "favorites";
  }) => {
    const url = new URL("/api/media/count", location.origin);
    if (params.kind) url.searchParams.set("kind", params.kind);
    if (params.tag) url.searchParams.set("tag", params.tag);
    if (params.tags && params.tags.length > 0) url.searchParams.set("tags", params.tags.join(","));
    if (params.excludeTags && params.excludeTags.length > 0)
      url.searchParams.set("excludeTags", params.excludeTags.join(","));
    if (params.q) url.searchParams.set("q", params.q);
    if (params.minSeconds != null) url.searchParams.set("minSeconds", String(params.minSeconds));
    if (params.maxSeconds != null) url.searchParams.set("maxSeconds", String(params.maxSeconds));
    if (params.set) url.searchParams.set("set", params.set);
    appendFilterParam(url, params.structured ?? null);
    return fetchJson<{ count: number }>(url.pathname + url.search);
  },
  bulkDeleteByQuery: (params: {
    q?: string;
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
  }) => {
    const { structured, tags, excludeTags, ...rest } = params;
    return fetchJson<{ ok: true; queued: number }>(`/api/admin/bulk/delete-by-query`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...rest,
        tags: tags && tags.length > 0 ? tags.join(",") : undefined,
        excludeTags: excludeTags && excludeTags.length > 0 ? excludeTags.join(",") : undefined,
        filter: encodeFilterParam(structured),
      }),
    });
  },
  random: (
    params: {
      kind?: "photo" | "video";
      set?: string;
      tags?: string[];
      excludeTags?: string[];
      exclude?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.kind) q.set("kind", params.kind);
    if (params.set) q.set("set", params.set);
    if (params.tags && params.tags.length > 0) q.set("tags", params.tags.join(","));
    if (params.excludeTags && params.excludeTags.length > 0)
      q.set("excludeTags", params.excludeTags.join(","));
    if (params.exclude != null) q.set("exclude", String(params.exclude));
    return fetchJson<{ id: number }>(`/api/media/random?${q}`);
  },
  neighbors: (
    id: number,
    set: string,
    opts: {
      minSeconds?: number;
      maxSeconds?: number;
      tags?: string[];
      excludeTags?: string[];
      nextMode?: "display" | "folder";
      order?: "newest" | "oldest" | "added_new" | "added_old" | "random";
      seed?: string;
      filter?: string;
    } = {},
  ) => {
    const q = new URLSearchParams({ set });
    if (opts.minSeconds != null) q.set("minSeconds", String(opts.minSeconds));
    if (opts.maxSeconds != null) q.set("maxSeconds", String(opts.maxSeconds));
    if (opts.tags && opts.tags.length > 0) q.set("tags", opts.tags.join(","));
    if (opts.excludeTags && opts.excludeTags.length > 0)
      q.set("excludeTags", opts.excludeTags.join(","));
    if (opts.nextMode) q.set("nextMode", opts.nextMode);
    if (opts.order) q.set("order", opts.order);
    if (opts.seed) q.set("seed", opts.seed);
    if (opts.filter) q.set("filter", opts.filter);
    return fetchJson<NeighborsResponse>(`/api/media/${id}/neighbors?${q}`);
  },
  setAppName: (value: string) =>
    fetchJson<{ ok: true; appName: string }>(`/api/admin/settings/app-name`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ value }),
    }),
  setHomeRefreshSeconds: (value: number) =>
    fetchJson<{ ok: true; value: number }>(`/api/admin/settings/home-refresh-seconds`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ value }),
    }),

  // Admin
  deleteMedia: (id: number) =>
    fetchJson<{ ok: true }>(`/api/media/${id}`, { method: "DELETE" }),

  // Admin user CRUD (only meaningful in AUTH_MODE=local)
  adminListUsers: () =>
    fetchJson<{
      users: Array<{
        id: string | number;
        username: string | null;
        email: string | null;
        display_name: string | null;
        is_admin_cached: boolean;
        has_password: boolean;
        last_seen_at: string | null;
        created_at: string;
        ai_credits_month: number;
      }>;
    }>(`/api/admin/users`),
  adminCreateUser: (body: {
    username: string;
    password: string;
    displayName?: string;
    isAdmin: boolean;
  }) =>
    fetchJson<{ ok: true; id: number }>(`/api/admin/users`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  adminPatchUser: (
    id: number,
    body: { password?: string; displayName?: string; isAdmin?: boolean },
  ) =>
    fetchJson<{ ok: true }>(`/api/admin/users/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  adminDeleteUser: (id: number) =>
    fetchJson<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),

  // Trash review (admin)
  listTrash: (params: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return fetchJson<{
      items: Array<{
        id: string | number;
        kind: "photo" | "video";
        relative_path: string;
        size_bytes: string | number;
        width: number | null;
        height: number | null;
        duration_ms: number | null;
        taken_at: string | null;
        deleted_at: string;
        deleted_by_user_id: string | number | null;
        deleted_by_name: string | null;
        deleted_by_email: string | null;
        trash_path: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/api/admin/trash?${q}`);
  },
  restoreFromTrash: (id: number) =>
    fetchJson<{ ok: true }>(`/api/admin/trash/${id}/restore`, { method: "POST" }),
  hardDeleteFromTrash: (id: number) =>
    fetchJson<{ ok: true }>(`/api/admin/trash/${id}`, { method: "DELETE" }),
  bulkRestoreTrash: (ids: number[]) =>
    fetchJson<{ ok: true; restored: number; failed: number }>(`/api/admin/trash/bulk-restore`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  bulkPurgeTrash: (ids: number[]) =>
    fetchJson<{ ok: true; purged: number; failed: number }>(`/api/admin/trash/bulk-purge`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids }),
    }),

  // Ingest trash review (findings deleted via the ingest panel)
  listIngestTrash: (params: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return fetchJson<{
      items: Array<{
        id: string | number;
        ingest_path: string;
        size_bytes: string | number;
        kind: string;
        reason: string | null;
        resolved_at: string | null;
        resolved_by_user_id: string | number | null;
        resolved_by_name: string | null;
        resolved_by_email: string | null;
        trash_path_exists: boolean;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>(`/api/admin/ingest-trash?${q}`);
  },
  restoreIngestTrash: (id: number) =>
    fetchJson<{ ok: true }>(`/api/admin/ingest-trash/${id}/restore`, { method: "POST" }),
  purgeIngestTrash: (id: number) =>
    fetchJson<{ ok: true }>(`/api/admin/ingest-trash/${id}`, { method: "DELETE" }),
  bulkRestoreIngestTrash: (ids: number[]) =>
    fetchJson<{ ok: true; restored: number; failed: number }>(`/api/admin/ingest-trash/bulk-restore`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  bulkPurgeIngestTrash: (ids: number[]) =>
    fetchJson<{ ok: true; purged: number; failed: number }>(`/api/admin/ingest-trash/bulk-purge`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids }),
    }),
  // Purge EVERYTHING in media trash (regardless of age). olderThanDays=0
  // makes the cutoff = now, which matches every soft-deleted row.
  purgeAllMediaTrash: () =>
    fetchJson<{ ok: true; purgedRows: number }>(`/api/admin/purge-trash`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ olderThanDays: 0 }),
    }),
  // ---- Video optimization (admin) ----
  getOptimize: () =>
    fetchJson<{
      cores: number;
      loadAvg: number[];
      config: {
        enabled: boolean;
        loadThreshold: number;
        idleSeconds: number;
        minSavingsPct: number;
        crf: number;
        minSizeBytes: number;
      };
      defaults: {
        loadThreshold: number;
        idleSeconds: number;
        minSavingsPct: number;
        crf: number;
        minSizeBytes: number;
      };
      stats: {
        pending: number;
        eligible: number;
        optimized: number;
        skipped: number;
        bytesSaved: number;
        recentSkips: Array<{ id: number; relativePath: string; reason: string }>;
        active: {
          mediaId: number;
          relativePath: string;
          originalSizeBytes: number;
          durationMs: number | null;
          startedAt: string;
          encodedMs: number;
          fps: number | null;
          speed: number | null;
        } | null;
      };
    }>(`/api/admin/optimize`),
  updateOptimize: (body: {
    enabled?: boolean;
    loadThreshold?: number;
    idleSeconds?: number;
    minSavingsPct?: number;
    crf?: number;
    minSizeBytes?: number;
  }) =>
    fetchJson<{ ok: true; applied: number }>(`/api/admin/optimize`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),

  // Admin-only: re-pend resolved-as-unsupported findings whose extension
  // is now in the supported whitelist (so a new ingest run picks them up).
  reclassifyUnsupported: () =>
    fetchJson<{ ok: true; scanned: number; restored: number }>(
      `/api/admin/ingest/reclassify-unsupported`,
      { method: "POST" },
    ),

  redetectAnimated: () =>
    fetchJson<{ ok: true; scanned: number; flagged: number; errored: number }>(
      `/api/admin/redetect-animated`,
      { method: "POST" },
    ),

  // Admin-only: hard delete a tag (and unlink it from every media item).
  deleteTag: (tagId: number) =>
    fetchJson<{ ok: true; unlinked: number }>(`/api/admin/tags/${tagId}`, {
      method: "DELETE",
    }),
  purgeAllIngestTrash: () =>
    fetchJson<{ ok: true; purgedFiles: number }>(`/api/admin/ingest-trash/purge-all`, {
      method: "POST",
    }),

  // Bulk (user-level)
  bulkFavorite: (mediaIds: number[], on: boolean) =>
    fetchJson<{ ok: true; count: number }>(`/api/media/bulk/favorite`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mediaIds, on }),
    }),
  bulkTag: (mediaIds: number[], addTagIds: number[], removeTagIds: number[] = []) =>
    fetchJson<{ ok: true; count: number }>(`/api/media/bulk/tag`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mediaIds, addTagIds, removeTagIds }),
    }),
  bulkDelete: (mediaIds: number[]) =>
    fetchJson<{ ok: true; count: number }>(`/api/admin/bulk/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mediaIds }),
    }),
  bulkFavoriteByQuery: (params: {
    q?: string;
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
    on: boolean;
  }) => {
    const { structured, tags, excludeTags, ...rest } = params;
    return fetchJson<{ ok: true; count: number }>(`/api/media/bulk/favorite-by-query`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...rest,
        tags: tags && tags.length > 0 ? tags.join(",") : undefined,
        excludeTags: excludeTags && excludeTags.length > 0 ? excludeTags.join(",") : undefined,
        filter: encodeFilterParam(structured),
      }),
    });
  },
  bulkTagByQuery: (params: {
    q?: string;
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
    addTagIds: number[];
    removeTagIds?: number[];
  }) => {
    const { structured, tags, excludeTags, ...rest } = params;
    return fetchJson<{ ok: true; count: number }>(`/api/media/bulk/tag-by-query`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...rest,
        removeTagIds: params.removeTagIds ?? [],
        tags: tags && tags.length > 0 ? tags.join(",") : undefined,
        excludeTags: excludeTags && excludeTags.length > 0 ? excludeTags.join(",") : undefined,
        filter: encodeFilterParam(structured),
      }),
    });
  },
  appendPlaylistByQuery: (playlistId: number, params: {
    q?: string;
    kind?: "photo" | "video";
    tag?: string;
    tags?: string[];
    excludeTags?: string[];
    minSeconds?: number;
    maxSeconds?: number;
    structured?: StructuredFilter | null;
  }) => {
    const { structured, tags, excludeTags, ...rest } = params;
    return fetchJson<{ ok: true; count: number }>(`/api/playlists/${playlistId}/append-by-query`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        ...rest,
        tags: tags && tags.length > 0 ? tags.join(",") : undefined,
        excludeTags: excludeTags && excludeTags.length > 0 ? excludeTags.join(",") : undefined,
        filter: encodeFilterParam(structured),
      }),
    });
  },

  // Playlists
  listPlaylists: () =>
    fetchJson<{
      playlists: Array<{
        id: number;
        name: string;
        description: string | null;
        is_public: boolean;
        created_at: string;
        updated_at: string;
        item_count: number;
        cover_media_id: number | null;
      }>;
    }>(`/api/playlists`),
  listPublicPlaylists: () =>
    fetchJson<{
      playlists: Array<{
        id: number;
        name: string;
        description: string | null;
        is_public: boolean;
        user_id: number;
        owner_display_name: string | null;
        created_at: string;
        updated_at: string;
        item_count: number;
        cover_media_id: number | null;
      }>;
    }>(`/api/playlists/public`),
  createPlaylist: (body: { name: string; description?: string; isPublic?: boolean }) =>
    fetchJson<{ id: number; name: string }>(`/api/playlists`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  getPlaylist: (id: number) =>
    fetchJson<{
      id: number;
      name: string;
      description: string | null;
      isPublic: boolean;
      isOwner: boolean;
      items: Array<{
        id: number;
        playlistItemId: number;
        position: number;
        kind: "photo" | "video";
        relativePath: string;
        width: number | null;
        height: number | null;
        durationMs: number | null;
        takenAt: string | null;
        sizeBytes: number;
        thumbUrl: string;
        posterUrl: string | null;
        isAnimated: boolean;
        originalUrl: string;
      }>;
    }>(`/api/playlists/${id}`),
  updatePlaylist: (id: number, body: { name?: string; description?: string; isPublic?: boolean }) =>
    fetchJson<{ id: number; name: string }>(`/api/playlists/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  deletePlaylist: (id: number) =>
    fetchJson<{ ok: true }>(`/api/playlists/${id}`, { method: "DELETE" }),
  addToPlaylist: (id: number, mediaIds: number[]) =>
    fetchJson<{ ok: true; added: number }>(`/api/playlists/${id}/items`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mediaIds }),
    }),
  removeFromPlaylist: (id: number, mediaId: number) =>
    fetchJson<{ ok: true }>(`/api/playlists/${id}/items/${mediaId}`, {
      method: "DELETE",
    }),
  reorderPlaylistItem: (id: number, mediaId: number, position: number) =>
    fetchJson<{ ok: true }>(`/api/playlists/${id}/items/${mediaId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ position }),
    }),
  reorderPlaylistAll: (id: number, mediaIds: number[]) =>
    fetchJson<{ ok: true; reordered: number }>(`/api/playlists/${id}/items/reorder`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mediaIds }),
    }),
  mediaDetail: (id: number) => fetchJson<MediaDetail>(`/api/media/${id}`),
  tags: () => fetchJson<TagSummary[]>(`/api/tags`),
  toggleFavorite: (id: number, on: boolean) =>
    fetchJson<{ ok: true }>(`/api/media/${id}/favorite`, {
      method: on ? "POST" : "DELETE",
    }),
  favoritesList: (params: { before?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.before) q.set("before", params.before);
    if (params.limit) q.set("limit", String(params.limit));
    return fetchJson<MediaListResponse>(`/api/favorites?${q}`);
  },
  createTag: (name: string) =>
    fetchJson<TagSummary>(`/api/tags`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    }),
  addTags: (mediaId: number, tagIds: number[]) =>
    fetchJson<{ ok: true }>(`/api/media/${mediaId}/tags`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ tagIds }),
    }),
  removeTag: (mediaId: number, tagId: number) =>
    fetchJson<{ ok: true }>(`/api/media/${mediaId}/tags/${tagId}`, {
      method: "DELETE",
    }),
  upload: async (
    file: File,
    onProgress?: (pct: number, phase: "hashing" | "uploading") => void,
    opts: { signal?: AbortSignal; uploadIdRef?: { current: string | null }; getTagIds?: () => number[] } = {},
  ): Promise<UploadOk | UploadDuplicate> => {
    return chunkedUpload(file, onProgress, opts);
  },
  ai: {
    status: () => fetchJson<AiStatus>("/api/ai/status"),
    models: () => fetchJson<{ models: AiModel[] }>("/api/ai/models"),
    price: (body: Omit<AiGenerateRequest, "prompt" | "negativePrompt" | "seed" | "examples"> & { exampleCount: number }) =>
      fetchJson<{ credits: number }>("/api/ai/price", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    generate: (body: AiGenerateRequest) =>
      fetchJson<{ generationId: number }>("/api/ai/generations", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      }),
    list: () => fetchJson<{ generations: AiGeneration[] }>("/api/ai/generations"),
    save: (imageId: number) =>
      fetchJson<{ mediaId: number; duplicate: boolean }>(`/api/ai/images/${imageId}/save`, { method: "POST" }),
    discard: async (imageId: number): Promise<void> => {
      const r = await fetch(`/api/ai/images/${imageId}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new ApiError(r.status, null);
    },
  },
};

interface InitResponse {
  uploadId: string;
  chunkSize: number;
  maxChunkSize: number;
  receivedBytes: number;
}

interface ChunkResponse {
  receivedBytes: number;
}

/**
 * Streams a File to the server in fixed-size chunks via init/PUT/finalize.
 *
 * Why chunked: a single multi-GB POST has to complete inside the proxy /
 * Fastify request timeout window, and any transient network blip means
 * starting over. Per-chunk PUTs each fit comfortably under the timeout, so
 * the upload tolerates flaky links and lets us resume mid-file.
 *
 * Sequential, not parallel: simpler server (strict offset append) and good
 * enough for a single-user session. Parallel chunk uploads would require
 * the server to seek+write at arbitrary offsets and serialize correctly.
 *
 * Retry policy: each chunk gets a few network-error retries with backoff.
 * On retry we re-query the server's current offset so we never resend
 * bytes it already accepted.
 */
async function chunkedUpload(
  file: File,
  onProgress?: (pct: number, phase: "hashing" | "uploading") => void,
  opts: { signal?: AbortSignal; uploadIdRef?: { current: string | null }; getTagIds?: () => number[] } = {},
): Promise<UploadOk | UploadDuplicate> {
  // Hash the file locally first so the server can detect a duplicate before we
  // send a single chunk. A hashing failure must never block the upload — fall
  // back to no pre-check (finalize still dedupes authoritatively).
  let sha256: string | undefined;
  try {
    if (onProgress) onProgress(0, "hashing");
    sha256 = await hashFileSha256(
      file,
      (frac) => {
        if (onProgress) onProgress(Math.round(frac * 100), "hashing");
      },
      opts.signal,
    );
  } catch (err) {
    // An abort during hashing must propagate as an abort — not silently fall
    // back to uploading the file we were asked to cancel.
    if (opts.signal?.aborted) throw new ApiError(0, "aborted");
    console.error("upload: local hashing failed, uploading without pre-check", err);
    sha256 = undefined;
  }

  // Abort that landed during hashing (which may have completed without throwing)
  // — bail before init so we don't create a server-side .part/.meta we'd then
  // have to reap.
  if (opts.signal?.aborted) throw new ApiError(0, "aborted");

  // Session tags for the init pre-check: if init reports a duplicate, the
  // server attaches these to the existing media. Read again at finalize time
  // for the upload path so tags added during a long upload still apply.
  const initRes = await fetch("/api/upload/init", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      filename: file.name,
      sizeBytes: file.size,
      sha256,
      tagIds: opts.getTagIds ? opts.getTagIds() : [],
    }),
  });
  // 409 from init = pre-upload duplicate. Surface it like finalize's duplicate.
  if (initRes.status === 409) {
    return (await initRes.json()) as UploadDuplicate;
  }
  if (!initRes.ok) {
    let body: unknown = null;
    try {
      body = await initRes.json();
    } catch {
      /* ignore */
    }
    throw new ApiError(initRes.status, body);
  }
  const init = (await initRes.json()) as InitResponse;
  if (opts.uploadIdRef) opts.uploadIdRef.current = init.uploadId;

  const chunkSize = Math.min(init.chunkSize, init.maxChunkSize);
  let offset = init.receivedBytes;

  while (offset < file.size) {
    if (opts.signal?.aborted) {
      // Best-effort cleanup; ignore failures.
      void fetch(`/api/upload/${init.uploadId}`, { method: "DELETE", credentials: "include" }).catch(() => {});
      throw new ApiError(0, "aborted");
    }
    const end = Math.min(offset + chunkSize, file.size);
    const slice = file.slice(offset, end);
    const accepted = await sendChunk(init.uploadId, offset, slice, opts.signal);
    if (accepted < end) {
      // Server reported it received fewer bytes than we sent — shouldn't
      // happen in practice, but bail rather than spin forever.
      throw new ApiError(0, "chunk_underrun");
    }
    offset = accepted;
    if (onProgress) onProgress(Math.min(99, Math.round((offset / file.size) * 100)), "uploading");
  }

  // Finalize can legitimately return 409 Conflict when the assembled
  // bytes hash to an already-imported file — that's not a failure, it's
  // a duplicate. fetchJson treats every non-2xx as a throw, so handle the
  // 409 explicitly and surface the existing-id payload to the caller.
  const finalRes = await fetch(`/api/upload/${init.uploadId}/finalize`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ tagIds: opts.getTagIds ? opts.getTagIds() : [] }),
  });
  if (onProgress) onProgress(100, "uploading");
  if (finalRes.status === 409 || finalRes.status === 201 || finalRes.status === 200) {
    return (await finalRes.json()) as UploadOk | UploadDuplicate;
  }
  let body: unknown = null;
  try {
    body = await finalRes.json();
  } catch {
    /* ignore */
  }
  throw new ApiError(finalRes.status, body);
}

async function sendChunk(
  uploadId: string,
  offset: number,
  blob: Blob,
  signal?: AbortSignal,
): Promise<number> {
  const url = `/api/upload/${uploadId}/chunk?offset=${offset}`;
  const maxAttempts = 4;
  let attempt = 0;
  let lastErr: unknown = null;
  while (attempt < maxAttempts) {
    attempt++;
    try {
      const r = await fetch(url, {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/octet-stream" },
        body: blob,
        signal,
      });
      if (r.status === 409) {
        // Offset mismatch — server has more (or fewer) bytes than we
        // expected. Re-query and adjust by trimming `blob`.
        const body = (await r.json()) as { expectedOffset?: number };
        const expected = body.expectedOffset;
        if (typeof expected === "number" && expected >= offset && expected <= offset + blob.size) {
          const skip = expected - offset;
          if (skip === blob.size) return expected; // server already has the whole chunk
          const remaining = blob.slice(skip);
          return sendChunk(uploadId, expected, remaining, signal);
        }
        throw new ApiError(409, body);
      }
      if (!r.ok) {
        let body: unknown = null;
        try {
          body = await r.json();
        } catch {
          /* ignore */
        }
        throw new ApiError(r.status, body);
      }
      const body = (await r.json()) as ChunkResponse;
      return body.receivedBytes;
    } catch (err) {
      lastErr = err;
      if (signal?.aborted) throw err;
      // Don't retry on auth or 4xx-ish status: those won't be fixed by
      // sending the same bytes again.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 409 && err.status !== 408) {
        throw err;
      }
      if (attempt >= maxAttempts) break;
      const delay = 250 * 2 ** (attempt - 1);
      await new Promise((res) => setTimeout(res, delay));
    }
  }
  throw lastErr instanceof Error ? lastErr : new ApiError(0, "chunk_failed");
}
